import type { ReactNode } from 'react'

import { createContext, useContext, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useKeydownShortcut } from '@/hooks/useKeydownShortcut'

export type TabType = 'recipe' | 'grocerylist';

export interface NavigationContextValue {
  activeTab: TabType
  navigateTab: (tab: TabType) => void
  setActiveTab: (tab: TabType) => void
}

export interface NavigationProviderProps {
  children: ReactNode
}

const NavigationContext = createContext<NavigationContextValue | null>(null)

export function NavigationProvider({ children }: NavigationProviderProps) {
  const navigate = useNavigate()

  const { mixId } = useParams()
  const [activeTab, setActiveTab] = useState<TabType>('recipe')

  const navigateTab = (tab: TabType) => {
    setActiveTab(tab)
    navigate(`/mixes/${mixId}/${tab}`)
  }

  const contextValue: NavigationContextValue = {
    activeTab,
    navigateTab,
    setActiveTab,
  }

  useKeydownShortcut(
    { key: 'R', ctrl: false, alt: false, shift: false, meta: false },
    () => {
      if (mixId) {
        setActiveTab('recipe')
        navigate(`/mixes/${mixId}/recipes`)
      }
    },
    'Toggle to Recipes',
    'Press R to switch to recipes'
  )

  useKeydownShortcut(
    { key: 'G', ctrl: false, alt: false, shift: false, meta: false },
    () => {
      if (mixId) {
        setActiveTab('grocerylist')
        navigate(`/mixes/${mixId}/grocerylist`)
      }
    },
    'Toggle to Grocery List',
    'Press G to switch to grocery list'
  )

  useKeydownShortcut(
    { key: 'M', ctrl: false, alt: false, shift: false, meta: false },
    () => {
      if (mixId) {
        setActiveTab('recipe')
        navigate(`/home`)
      }
    },
    'Toggle to Mix List',
    'Press M to switch to mix list'
  )

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  )
}

export function useNavigationContext(): NavigationContextValue {
  const context = useContext(NavigationContext)
  if (!context) {
    throw new Error('useNavigationContext must be used within a NavigationProvider')
  }
  return context
}
